import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Crown, Percent, Gift, Bell, Play, Check, Star, Award } from "lucide-react";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";

const VIPPage = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const navigate = useNavigate();

  const handleSubscribe = async (plan = selectedPlan) => {
    if (!user) {
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/vip/subscribe`, {
        origin_url: window.location.origin,
        plan: plan
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start subscription. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24">
      <section className="relative py-20 overflow-hidden" data-testid="vip-hero">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1765211002533-abda20c22651" alt="VIP Background" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="mb-8 inline-block">
            <img src="https://customer-assets.emergentagent.com/job_3ae66389-b093-4330-9743-6c6a00dc5113/artifacts/1c3nvn39_ChatGPT%20Image%20Dec%2031%2C%202025%2C%2002_53_09%20PM.png" alt="VIP Elite Crew Badge" className="h-40 w-auto mx-auto" />
          </div>
          <h1 className="font-heading text-5xl sm:text-6xl gold-text mb-4">ELITE POLISHING CREW</h1>
          <p className="text-xl text-gray-300 mb-2">VIP Membership</p>
          <p className="text-gray-400 mb-8">Earn your place. Get exclusive access. Cancel anytime.</p>

          {user?.is_vip ? (
            <div className="bg-[#D4AF37]/20 border border-[#D4AF37] rounded-sm p-6 inline-block">
              <div className="flex items-center gap-3 text-[#D4AF37]">
                <Crown className="w-6 h-6" />
                <span className="font-bold text-lg">You're Part of the Elite Crew!</span>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto">
                <div onClick={() => setSelectedPlan('monthly')} className={`flex-1 p-6 rounded-sm border-2 cursor-pointer transition-all ${selectedPlan === 'monthly' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 bg-[#121212] hover:border-white/30'}`}>
                  <h3 className="font-heading text-2xl mb-2">MONTHLY</h3>
                  <p className="text-3xl font-bold text-[#D4AF37] mb-1">$3.99<span className="text-base font-normal text-gray-400">/mo</span></p>
                  <p className="text-gray-500 text-sm">Billed monthly</p>
                </div>
                <div onClick={() => setSelectedPlan('yearly')} className={`flex-1 p-6 rounded-sm border-2 cursor-pointer transition-all relative ${selectedPlan === 'yearly' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 bg-[#121212] hover:border-white/30'}`}>
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D62828] text-white text-xs px-3 py-1 rounded-full font-bold">SAVE $7.88</span>
                  <h3 className="font-heading text-2xl mb-2">YEARLY</h3>
                  <p className="text-3xl font-bold text-[#D4AF37] mb-1">$40.00<span className="text-base font-normal text-gray-400">/yr</span></p>
                  <p className="text-gray-500 text-sm">Only $3.33/month</p>
                </div>
              </div>
              <button onClick={() => handleSubscribe(selectedPlan)} disabled={loading} className="bg-[#D4AF37] hover:bg-[#B5952F] text-black px-12 py-4 rounded-sm font-bold uppercase tracking-wider text-lg transition-all disabled:opacity-50" data-testid="vip-subscribe-btn">
                {loading ? 'Processing...' : `Join the Elite Crew - ${selectedPlan === 'monthly' ? '$3.99/mo' : '$40/yr'}`}
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-[#0F0F0F]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl text-center mb-4">STARTER AUTHORITY KIT</h2>
          <p className="text-center text-gray-400 mb-12">Every new VIP member receives</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { id: 'kit-sticker', icon: <Star className="w-8 h-8" />, title: 'VIP Sticker', desc: 'Elite Crew badge for your toolbox or fuel tank' },
              { id: 'kit-card', icon: <Gift className="w-8 h-8" />, title: 'Welcome Card', desc: 'Personalized welcome to the crew' },
              { id: 'kit-discount', icon: <Percent className="w-8 h-8" />, title: '$5-$10 Discount', desc: 'VIP-only discount code on first order' },
              { id: 'kit-video', icon: <Play className="w-8 h-8" />, title: 'Polishing 101', desc: 'QR card to exclusive beginner video' },
            ].map((item) => (
              <div key={item.id} className="bg-[#1A1A1A] border border-white/5 p-6 rounded-sm text-center">
                <div className="text-[#D4AF37] mb-3 flex justify-center">{item.icon}</div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0A0A0A]" data-testid="vip-benefits">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl text-center mb-4">MONTHLY VIP PERKS</h2>
          <p className="text-center text-gray-400 mb-12">Every month you get more value</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="vip-glow bg-[#121212] p-8 rounded-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center"><Play className="w-8 h-8 text-black" /></div>
                <div>
                  <h3 className="font-heading text-2xl">Jimmy's Tip of the Month</h3>
                  <p className="text-gray-400 text-sm">35+ years of wisdom, one video at a time</p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-300">
                {['Exclusive 5-10 minute video each month', 'Topics: Avoiding swirl marks, choosing compounds, etc.', 'Downloadable checklist or tip sheet'].map((t, i) => (
                  <li key={i} className="flex items-center gap-2"><Check className="w-4 h-4 text-[#D4AF37]" />{t}</li>
                ))}
              </ul>
            </div>
            <div className="vip-glow bg-[#121212] p-8 rounded-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#D62828] rounded-full flex items-center justify-center"><Gift className="w-8 h-8 text-white" /></div>
                <div>
                  <h3 className="font-heading text-2xl">Rotating Monthly Perks</h3>
                  <p className="text-gray-400 text-sm">Something new every month</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[{ id: 'month-1', month: 'Month 1', perk: 'Free Shipping' }, { id: 'month-2', month: 'Month 2', perk: 'Sample Compound' }, { id: 'month-3', month: 'Month 3', perk: '$5-$10 Credit' }, { id: 'month-4', month: 'Month 4', perk: 'Giveaway Entry' }].map((item) => (
                  <div key={item.id} className="bg-[#0F0F0F] p-3 rounded-sm text-center">
                    <p className="text-[10px] text-gray-500 uppercase">{item.month}</p>
                    <p className="text-sm font-semibold">{item.perk}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { id: 'benefit-discounts', icon: <Percent className="w-10 h-10" />, title: 'VIP-Only Discounts', desc: 'Exclusive pricing on select polishing & detailing products' },
              { id: 'benefit-early-access', icon: <Bell className="w-10 h-10" />, title: 'Early Access', desc: 'Be first to know about new releases & limited drops' },
              { id: 'benefit-videos', icon: <Play className="w-10 h-10" />, title: 'Exclusive Videos', desc: 'Access to VIP-only Polishing University content' },
              { id: 'benefit-elite', icon: <Award className="w-10 h-10" />, title: 'Elite Status', desc: 'Premium support and recognition in the community' },
            ].map((benefit) => (
              <div key={benefit.id} className="bg-[#121212] border border-white/10 p-6 rounded-sm text-center">
                <div className="text-[#D4AF37] mb-4 flex justify-center">{benefit.icon}</div>
                <h3 className="font-heading text-lg mb-2">{benefit.title}</h3>
                <p className="text-gray-400 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl mb-4">YOUR VIP BADGE</h2>
          <p className="text-gray-400 mb-8">Proudly display your Elite Crew membership</p>
          <div className="flex justify-center gap-8 flex-wrap">
            <img src="https://customer-assets.emergentagent.com/job_3ae66389-b093-4330-9743-6c6a00dc5113/artifacts/i92x6pa2_vip%20logos.jpeg" alt="VIP Badge Designs" className="max-w-md w-full rounded-sm border border-white/10" />
          </div>
          <p className="text-gray-500 text-sm mt-6">Sticker included in your welcome kit. Perfect for toolboxes, fuel tanks, shop cabinets.</p>
        </div>
      </section>

      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-heading text-4xl text-center mb-12">FAQ</h2>
          <div className="space-y-4">
            {[
              { q: 'How does billing work?', a: 'Choose between monthly ($3.99/mo) or yearly ($40/yr) billing. Your membership auto-renews until you cancel.' },
              { q: 'Can I cancel anytime?', a: 'Yes! Cancel your membership anytime from your account settings. No questions asked.' },
              { q: 'How do I access VIP content?', a: 'Once subscribed, VIP content will automatically unlock in Polishing University and the shop.' },
              { q: 'What payment methods do you accept?', a: 'We accept all major credit cards through our secure Stripe checkout.' },
            ].map((faq, idx) => (
              <div key={idx} className="bg-[#121212] border border-white/5 rounded-sm p-6">
                <h4 className="font-semibold mb-2">{faq.q}</h4>
                <p className="text-gray-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default VIPPage;
